<template>
  <div>
    <section v-if="isLoading">
      <div class="fixed-center">
        <b-spinner></b-spinner>
      </div>
    </section>

    <section v-else-if="tags.length < 1">
      <p>
        You don't have any tags in your system yet. <br />Create your first one
        by clicking "New Tag."
      </p>
      <b-button v-b-modal.tagModal style="margin-top: 4px">New Tag</b-button>
    </section>

    <section v-else>
      <div>
        <b-row
          class="search-header"
          align-h="center"
          style="margin-bottom: 0px !important"
        >
          <b-col md="6" class="my-1">
            <b-input-group>
              <b-form-input
                v-model="searchQuery"
                placeholder="Search tags..."
              />
            </b-input-group>
          </b-col>
          <b-col md="2">
            <b-button v-b-modal.tagModal style="margin-top: 4px"
              >New Tag</b-button
            >
          </b-col>
          <b-col md="4" class="text-right">
            <div class="tag-metrics">
              <span class="metric-item">
                <strong>{{ totalTags }}</strong> Total
              </span>
              <span class="metric-item">
                <strong>{{ usedTags }}</strong> In Use
              </span>
              <span class="metric-item">
                <strong>{{ unusedTags }}</strong> Unused
              </span>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="tagsTable"
        :items="filteredTags"
        :fields="fields"
        hover
        striped
        @row-clicked="rowClicked"
        fixed
      >
        <template #cell(usage)="data">
          <span v-if="getTagUsage(data.item.id).total > 0" class="usage-badge">
            {{ getTagUsage(data.item.id).total }} uses
          </span>
          <span v-else class="usage-badge unused">Unused</span>
        </template>

        <template #cell(actions)="data">
          <div class="action-buttons">
            <b-button
              size="sm"
              variant="outline-primary"
              @click.stop="startEdit(data.item)"
              v-b-tooltip.hover.top="'Edit tag'"
            >
              <i class="fi-pencil"></i>
            </b-button>
            <b-button
              size="sm"
              variant="outline-info"
              @click.stop="showTagDetails(data.item)"
              v-b-tooltip.hover.top="'View usage details'"
            >
              <i class="fi-info"></i>
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              @click.stop="confirmDelete(data.item)"
              :disabled="getTagUsage(data.item.id).total > 0"
              v-b-tooltip.hover.top="
                getTagUsage(data.item.id).total > 0
                  ? 'Cannot delete tag in use'
                  : 'Delete tag'
              "
            >
              <i class="fi-trash"></i>
            </b-button>
          </div>
        </template>
      </b-table>
    </section>

    <!-- Tag Modal -->
    <b-modal
      id="tagModal"
      :title="editingTag ? 'Edit Tag' : 'New Tag'"
      @ok="saveTag"
      @hidden="resetForm"
      :ok-disabled="!tagFormValidation"
    >
      <b-form @submit.prevent="saveTag">
        <b-form-group label="Tag Name" label-for="tag-name">
          <b-form-input
            id="tag-name"
            v-model="formTagName"
            :state="tagFormValidation"
            placeholder="Enter tag name..."
            required
          />
          <b-form-invalid-feedback>
            Tag name must be at least 2 characters and unique
          </b-form-invalid-feedback>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Tag Details Modal -->
    <b-modal
      v-model="showDetailsModal"
      :title="selectedTagModalTitle"
      size="lg"
      hide-footer
    >
      <div v-if="selectedTag" class="tag-details">
        <div class="usage-summary">
          <h6>Usage Breakdown</h6>
          <div class="usage-stats">
            <div class="usage-stat">
              <strong>{{ getTagUsage(selectedTag.id).items }}</strong>
              <span>Items</span>
            </div>
            <div class="usage-stat">
              <strong>{{ getTagUsage(selectedTag.id).menus }}</strong>
              <span>Menu Categories</span>
            </div>
            <div class="usage-stat">
              <strong>{{ getTagUsage(selectedTag.id).packages }}</strong>
              <span>Packages</span>
            </div>
            <div class="usage-stat">
              <strong>{{ getTagUsage(selectedTag.id).units }}</strong>
              <span>Units</span>
            </div>
            <div class="usage-stat">
              <strong>{{ getTagUsage(selectedTag.id).modifiers }}</strong>
              <span>Modifiers</span>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirm Delete"
      @ok="deleteTag"
      ok-variant="danger"
      ok-title="Delete"
    >
      <p>Are you sure you want to delete the tag "{{ tagToDeleteName }}"?</p>
      <p class="text-muted">This action cannot be undone.</p>
    </b-modal>

    <div v-if="isRequesting" class="loading-overlay">
      <b-spinner></b-spinner>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import AlertMixin from "@/mixins/alert-mixin";
import API from "@/api";
import axios from "axios";

export default {
  name: "TagManagement",

  mixins: [AlertMixin],

  async mounted() {
    await this.fetchTags();
    this.loadTagUsageData();
  },

  data() {
    return {
      formTagName: "",
      editingTag: null,
      searchQuery: "",
      selectedTag: null,
      showDetailsModal: false,
      showDeleteModal: false,
      tagToDelete: null,
      tagUsageData: {},
      fields: [
        {
          key: "name",
          label: "Tag Name",
        },
        {
          key: "usage",
          label: "Usage",
        },
        {
          key: "actions",
          label: "Actions",
          class: "text-center",
        },
      ],
    };
  },

  computed: {
    ...mapState({
      tags: (state) => state.tags.tags,
      isRequesting: (state) => state.tags.isRequesting,
      isLoading: (state) => state.tags.isRequesting,
    }),

    totalTags() {
      return this.tags.length;
    },

    usedTags() {
      return this.tags.filter((tag) => this.getTagUsage(tag.id).total > 0)
        .length;
    },

    unusedTags() {
      return this.totalTags - this.usedTags;
    },

    filteredTags() {
      if (!this.searchQuery) return this.tags;

      const query = this.searchQuery.toLowerCase();
      return this.tags.filter((tag) => tag.name.toLowerCase().includes(query));
    },

    tagFormValidation() {
      if (!this.formTagName) return null;

      const isValid =
        this.formTagName.length >= 2 &&
        !this.tags.some(
          (tag) =>
            tag.name.toLowerCase() === this.formTagName.toLowerCase() &&
            tag.id !== this.editingTag
        );
      return isValid;
    },

    selectedTagModalTitle() {
      return this.selectedTag ? `Tag Details: ${this.selectedTag.name}` : "";
    },

    tagToDeleteName() {
      return this.tagToDelete ? this.tagToDelete.name : "";
    },
  },

  methods: {
    ...mapActions(["fetchTags", "createTag", "updateTag", "deleteTag"]),

    rowClicked(tag) {
      this.startEdit(tag);
    },

    startEdit(tag) {
      this.editingTag = tag.id;
      this.formTagName = tag.name;
      this.$bvModal.show("tagModal");
    },

    resetForm() {
      this.editingTag = null;
      this.formTagName = "";
    },

    async saveTag() {
      try {
        if (this.editingTag) {
          await this.updateTag({
            id: this.editingTag,
            name: this.formTagName,
          });
          this.successNotification("Tag updated successfully!");
        } else {
          await this.createTag({ name: this.formTagName });
          this.successNotification("Tag created successfully!");
        }
        this.$bvModal.hide("tagModal");
        this.loadTagUsageData(); // Refresh usage data
      } catch (error) {
        this.errorNotification(
          this.editingTag ? "Failed to update tag" : "Failed to create tag"
        );
      }
    },

    confirmDelete(tag) {
      this.tagToDelete = tag;
      this.showDeleteModal = true;
    },

    async deleteTag() {
      try {
        await this.$store.dispatch("deleteTag", this.tagToDelete.id);
        this.showDeleteModal = false;
        this.tagToDelete = null;
        this.successNotification("Tag deleted successfully!");
        this.loadTagUsageData(); // Refresh usage data
      } catch (error) {
        this.errorNotification("Failed to delete tag");
      }
    },

    showTagDetails(tag) {
      this.selectedTag = tag;
      this.showDetailsModal = true;
    },

    getTagUsage(tagId) {
      const usage = this.tagUsageData[tagId] || {
        items: 0,
        menus: 0,
        packages: 0,
        units: 0,
        modifiers: 0,
        total: 0,
      };
      return usage;
    },

    async loadTagUsageData() {
      try {
        // Fetch usage data from all tag relationship APIs
        const [itemTags, menuTags, packageTags, unitTags, modifierTags] =
          await Promise.all([
            axios.get(`${API.BASE_URL}${API.ITEM_TAGS}`, API.RequestHeaders()),
            axios.get(`${API.BASE_URL}${API.MENU_TAGS}`, API.RequestHeaders()),
            axios.get(
              `${API.BASE_URL}${API.PACKAGETAGS}`,
              API.RequestHeaders()
            ),
            axios.get(`${API.BASE_URL}/unittags`, API.RequestHeaders()),
            axios.get(
              `${API.BASE_URL}${API.MODIFIER_TAGS}`,
              API.RequestHeaders()
            ),
          ]);

        // Process the data to count usage per tag
        this.tagUsageData = {};

        // Count item usage
        if (itemTags.data) {
          itemTags.data.forEach((itemTag) => {
            if (!this.tagUsageData[itemTag.tag_id]) {
              this.tagUsageData[itemTag.tag_id] = {
                items: 0,
                menus: 0,
                packages: 0,
                units: 0,
                modifiers: 0,
                total: 0,
              };
            }
            this.tagUsageData[itemTag.tag_id].items++;
            this.tagUsageData[itemTag.tag_id].total++;
          });
        }

        // Count menu usage
        if (menuTags.data) {
          menuTags.data.forEach((menuTag) => {
            if (!this.tagUsageData[menuTag.tag_id]) {
              this.tagUsageData[menuTag.tag_id] = {
                items: 0,
                menus: 0,
                packages: 0,
                units: 0,
                modifiers: 0,
                total: 0,
              };
            }
            this.tagUsageData[menuTag.tag_id].menus++;
            this.tagUsageData[menuTag.tag_id].total++;
          });
        }

        // Count package usage
        if (packageTags.data) {
          packageTags.data.forEach((packageTag) => {
            if (!this.tagUsageData[packageTag.tag_id]) {
              this.tagUsageData[packageTag.tag_id] = {
                items: 0,
                menus: 0,
                packages: 0,
                units: 0,
                modifiers: 0,
                total: 0,
              };
            }
            this.tagUsageData[packageTag.tag_id].packages++;
            this.tagUsageData[packageTag.tag_id].total++;
          });
        }

        // Count unit (room) usage
        if (unitTags.data) {
          unitTags.data.forEach((unitTag) => {
            if (!this.tagUsageData[unitTag.tag_id]) {
              this.tagUsageData[unitTag.tag_id] = {
                items: 0,
                menus: 0,
                packages: 0,
                units: 0,
                modifiers: 0,
                total: 0,
              };
            }
            this.tagUsageData[unitTag.tag_id].units++;
            this.tagUsageData[unitTag.tag_id].total++;
          });
        }

        // Count modifier (tax/fee) usage
        if (modifierTags.data) {
          modifierTags.data.forEach((modifierTag) => {
            if (!this.tagUsageData[modifierTag.tag_id]) {
              this.tagUsageData[modifierTag.tag_id] = {
                items: 0,
                menus: 0,
                packages: 0,
                units: 0,
                modifiers: 0,
                total: 0,
              };
            }
            this.tagUsageData[modifierTag.tag_id].modifiers++;
            this.tagUsageData[modifierTag.tag_id].total++;
          });
        }
      } catch (error) {
        console.error("Failed to load tag usage data:", error);
        this.errorNotification("Failed to load tag usage data");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-header {
  margin-bottom: 1rem;
}

.tag-metrics {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: flex-end;

  .metric-item {
    font-size: 0.9rem;
    color: #6c757d;

    strong {
      color: #007bff;
      font-weight: 600;
    }
  }
}

.usage-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #007bff;
  color: white;
  border-radius: 12px;
  font-size: 0.75rem;

  &.unused {
    background: #6c757d;
  }
}

.action-buttons {
  display: flex;
  gap: 0.25rem;
  justify-content: center;
}

.tag-details {
  .usage-summary {
    h6 {
      margin-bottom: 1rem;
      color: #2c3e50;
    }

    .usage-stats {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;

      .usage-stat {
        text-align: center;
        padding: 1rem;
        background: #f8f9fa;
        border-radius: 8px;
        min-width: 80px;

        strong {
          display: block;
          font-size: 1.5rem;
          color: #007bff;
        }

        span {
          font-size: 0.85rem;
          color: #6c757d;
        }
      }
    }
  }
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.fixed-center {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

@media (max-width: 768px) {
  .tag-metrics {
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>
